import type { FeatureTab } from "./help-content.ts";

export type FleetGuideExamplePhase = {
  title: string;
  summary: string;
  steps: string[];
  relatedTab?: FeatureTab;
  /** Tab-specific interactive example (see fleet-guide-tries.ts). */
  tryId?: string;
};

export interface FleetGuideExample {
  title: string;
  intro: string;
  phases: FleetGuideExamplePhase[];
  outcome: string;
}

/** Walkthrough: use Fleet Control + multi-agent chat to scaffold a small app. */
export const FLEET_GUIDE_EXAMPLE: FleetGuideExample = {
  title: "Example: simple app with multiple agents",
  intro:
    "This walkthrough builds a tiny Node.js todo CLI in a fresh folder. One lead agent plans and delegates; subagents implement and test in parallel. You stay in Chat the whole time.",
  phases: [
    {
      title: "1. Connect the gateway",
      summary: "The gateway must be online before any agent run.",
      steps: [
        "Open Overview and set the gateway URL (e.g. ws://127.0.0.1:18789).",
        "Paste your gateway token and connect until Jaisiu shows Online.",
        "Approve device pairing if the gateway prompts you.",
      ],
      relatedTab: "overview",
      tryId: "example-overview-1",
    },
    {
      title: "2. Point an agent at a workspace",
      summary: "Agents write files on the machine where the gateway runs.",
      steps: [
        "Open Agents and select your main agent (often main).",
        "Set workspace to an empty directory, e.g. ~/projects/todo-cli-demo.",
        "Save; confirm Files panel can list that path.",
      ],
      relatedTab: "agents",
      tryId: "example-agents-1",
    },
    {
      title: "3. Enable skills (optional)",
      summary: "Coding and shell skills let the agent edit files and run tests.",
      steps: [
        "Open Skills and enable skills your deployment allows (e.g. coding, terminal).",
        "Provide API keys only in the secure fields shown — never in Chat.",
      ],
      relatedTab: "skills",
      tryId: "example-skills-1",
    },
    {
      title: "4. Send a multi-agent task in Chat",
      summary:
        "Use @all (or @agentId / /best-of-n) so the gateway fans out work; subagents appear as expandable blocks in the thread.",
      steps: [
        "Open Chat and select the main agent session (e.g. agent:main:main).",
        "Send an explicit orchestration prefix, for example:",
        "@all Build a minimal Node.js todo CLI in this workspace. One subagent writes src/ and package.json; another writes a smoke test and runs npm test. Summarize when done.",
        "If you type a multi-agent phrase without @all, use the suggestion banner → Use @all before sending.",
        "Watch subagent cards in the transcript; expand them or use Open session tab for a child session.",
        "Use Abort if a run loops or you need to correct course.",
      ],
      relatedTab: "chat",
      tryId: "example-chat-1",
    },
    {
      title: "5. Tune routing (optional)",
      summary: "Workforce rules help recurring delegation patterns across the fleet.",
      steps: [
        "Open Workforce and review routing / delegation rules if your gateway exposes them.",
        "Adjust rules when you want specific skills or agents for implementation vs review.",
        "Re-test with the same Chat prompt to confirm routing.",
      ],
      relatedTab: "workforce",
      tryId: "example-workforce-1",
    },
    {
      title: "6. Verify, clean up sessions, and schedule",
      summary: "Confirm the app exists; delete stale demo sessions when finished.",
      steps: [
        "In Agents → Files, confirm package.json and src/ were created.",
        "Open Sessions to see session keys used by the run, or use Chat tab ⋯ → Delete session to remove one.",
        "Optional: add a Cron job to run npm test daily on that session.",
        "Use Logs or Debug if something failed mid-run.",
      ],
      relatedTab: "sessions",
      tryId: "example-sessions-1",
    },
  ],
  outcome:
    "You end with a small todo CLI on disk, a visible multi-agent transcript in Chat, and optional Cron for regression checks — all without leaving Chat.",
};
